// MA-155 — reversal-stop adaptive placement staircase, the reusable primitive.
//
// ADR-053 (MA-64) shipped per-item branching on the live 5-item onboarding
// diagnostic but deliberately deferred "stop on a stable estimate within
// ~8-12 items" as a follow-up requiring a genuine mid-session adaptive item
// runner — this module is that follow-up. It is pure, domain-agnostic logic
// over an abstract rung index [0, ladderSize-1]; it has no live caller yet
// (see ADR-058 §Deliberate scope: zero live caller this round).
//
// Reversal definition: a reversal is counted when the INTENDED step direction
// (up on correct, down on incorrect) flips relative to the previous step's
// intended direction — not when the clamped rung fails to actually move. A
// child pinned at the ladder ceiling who keeps answering correctly is not
// "reversing" just because the rung can't climb any higher; only a real
// correct→incorrect (or incorrect→correct) flip counts. See
// adaptiveStaircase.test.mjs's boundary-clamp tests for the failure mode this
// avoids.

interface StaircaseConfig {
  /** Number of rungs; valid rung indices are [0, ladderSize - 1]. */
  ladderSize: number;
  seedRung: number;
  minItems: number;
  maxItems: number;
  reversalStopCount: number;
}

interface StaircaseState {
  rung: number;
  itemsServed: number;
  reversalCount: number;
  /** Intended direction of the last answered item (+1 up / -1 down); null before the first answer. */
  lastDirection: 1 | -1 | null;
  done: boolean;
}

interface StaircaseResult {
  finalRung: number;
  itemsServed: number;
  reversalCount: number;
  /** The rung served at each item, in order (length === itemsServed). */
  trace: number[];
}

export function initStaircase(config: StaircaseConfig): StaircaseState {
  const seed = Math.max(0, Math.min(config.ladderSize - 1, config.seedRung));
  return { rung: seed, itemsServed: 0, reversalCount: 0, lastDirection: null, done: false };
}

/** Answer one served item at state.rung; returns the next state. Pure —
 *  callers own their own state, so concurrent math/reading runs never share
 *  mutable data. */
export function stepStaircase(
  config: StaircaseConfig,
  state: StaircaseState,
  wasCorrect: boolean,
): StaircaseState {
  const direction: 1 | -1 = wasCorrect ? 1 : -1;
  const reversed = state.lastDirection !== null && direction !== state.lastDirection;
  const reversalCount = state.reversalCount + (reversed ? 1 : 0);
  const rung = Math.max(0, Math.min(config.ladderSize - 1, state.rung + direction));
  const itemsServed = state.itemsServed + 1;
  const done =
    itemsServed >= config.maxItems ||
    (itemsServed >= config.minItems && reversalCount >= config.reversalStopCount);
  return { rung, itemsServed, reversalCount, lastDirection: direction, done };
}

/** Test/simulation driver: serves items via `answerOracle(rung) => wasCorrect`
 *  until the staircase stops. Not itself a live caller — a real screen would
 *  drive `stepStaircase` directly from an actual answered item, one item at a
 *  time, across renders. */
export function runStaircase(
  config: StaircaseConfig,
  answerOracle: (rung: number) => boolean,
): StaircaseResult {
  let state = initStaircase(config);
  const trace: number[] = [];
  // eslint-disable-next-line no-constant-condition
  while (true) {
    trace.push(state.rung);
    const wasCorrect = answerOracle(state.rung);
    state = stepStaircase(config, state, wasCorrect);
    if (state.done) break;
  }
  return {
    finalRung: state.rung,
    itemsServed: state.itemsServed,
    reversalCount: state.reversalCount,
    trace,
  };
}

/** Sorted, deduplicated difficulty values a module ladder spans — the rung
 *  space a staircase steps over for that domain. Verified (not assumed) by
 *  the bite test to be contiguous [0..8] for both MATH_MODULES and
 *  READING_MODULES. */
export function difficultyRungsFor(modules: { difficulty: number }[]): number[] {
  return Array.from(new Set(modules.map((m) => m.difficulty))).sort((a, b) => a - b);
}

/** The first real module at a given rung's difficulty tier — for a future
 *  live wiring to pick concrete content, and exercised here so `rungs`
 *  produced by the staircase are proven to map to real curriculum content,
 *  not just abstract indices. */
export function moduleAtRung<T extends { difficulty: number }>(
  modules: T[],
  rungs: number[],
  rungIndex: number,
): T | null {
  const targetDifficulty = rungs[rungIndex];
  return modules.find((m) => m.difficulty === targetDifficulty) ?? null;
}

// v1 calibration constants (ADR-058) — sized for a 9-rung (difficulty 0-8)
// module ladder, not a cited psychometric threshold.
export const STAIRCASE_MIN_ITEMS = 8;
export const STAIRCASE_MAX_ITEMS = 12;
export const STAIRCASE_REVERSAL_STOP_COUNT = 3;
